/**
 * Demo Mode Store
 *
 * Manages demo mode state -- active flag, current persona, and saved session
 * for seamless entry/exit. Used by the DemoModeBar and DemoLanding page to
 * drive the demo experience. Only functional when mock API is active.
 *
 * When entering demo mode, the store snapshots the current auth session,
 * resets the mock database, and logs in as the selected persona. On exit,
 * the previous session is restored. Supports 6 personas: Student, Teacher,
 * Parent, School Admin, Vendor, and Platform Admin.
 *
 * @module useDemoModeStore
 *
 * @example
 * ```tsx
 * import { useDemoModeStore, getDemoPersona, DEMO_PERSONAS } from '@/store/useDemoModeStore'
 *
 * function DemoBar() {
 *   const { isActive, activePersonaKey, switchPersona, exitDemo } = useDemoModeStore()
 *   const navigate = useNavigate()
 *
 *   if (!isActive) return null
 *
 *   return (
 *     <div>
 *       Demo: {getDemoPersona(activePersonaKey!)?.label}
 *       <button onClick={() => switchPersona('teacher', navigate)}>Switch to Teacher</button>
 *       <button onClick={() => exitDemo(navigate)}>Exit Demo</button>
 *     </div>
 *   )
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { NavigateFunction } from 'react-router-dom'
import { useAuthStore } from './useAuthStore'
import { resetDatabase } from '../api/seed'
import { getContextHomeRoute } from '../lib/contextHelpers'

export interface DemoPersona {
  key: string
  label: string
  email: string
  password: string
  description: string
}

export const DEMO_PERSONAS: DemoPersona[] = [
  {
    key: 'student',
    label: 'Student',
    email: 'student@lincoln-elementary.edu',
    password: 'DcStudent_2026!',
    description: 'School-linked student with challenges, communities, and XP progress',
  },
  {
    key: 'teacher',
    label: 'Teacher',
    email: 'teacher@lincoln-elementary.edu',
    password: 'DcTeach_2026!',
    description: 'Assigns challenges, reviews track records, monitors class progress',
  },
  {
    key: 'parent',
    label: 'Parent',
    email: 'parent@example.com',
    password: 'DcParent_2026!',
    description: 'Monitors child activity, approves content, manages linked accounts',
  },
  {
    key: 'school_admin',
    label: 'School Admin',
    email: 'admin@lincoln-elementary.edu',
    password: 'DcAdmin_2026!',
    description: 'Manages roster, teachers, classes, and school-wide settings',
  },
  {
    key: 'vendor',
    label: 'Vendor',
    email: 'vendor@craftbox.com',
    password: 'DcVendor_2026!',
    description: 'Creates paid challenges, manages orders, views analytics',
  },
  {
    key: 'platform_admin',
    label: 'Platform Admin',
    email: 'platform@docurious.com',
    password: 'DcPlatfm_2026!',
    description: 'Full platform oversight — users, content, verification, analytics',
  },
]

export function getDemoPersona(key: string): DemoPersona | undefined {
  return DEMO_PERSONAS.find((p) => p.key === key)
}

interface DemoModeState {
  isActive: boolean
  activePersonaKey: string | null
  savedSession: string | null
  savedAuthStore: string | null
  isTransitioning: boolean
}

interface DemoModeActions {
  enterDemo: (personaKey: string, navigate: NavigateFunction) => Promise<void>
  switchPersona: (personaKey: string, navigate: NavigateFunction) => Promise<void>
  exitDemo: (navigate: NavigateFunction) => void
  resetDemoData: () => Promise<void>
}

type DemoModeStore = DemoModeState & DemoModeActions

export const useDemoModeStore = createWithEqualityFn<DemoModeStore>()(
  persist(
    (set, get) => ({
      isActive: false,
      activePersonaKey: null,
      savedSession: null,
      savedAuthStore: null,
      isTransitioning: false,

      enterDemo: async (personaKey: string, navigate: NavigateFunction) => {
        const persona = getDemoPersona(personaKey)
        if (!persona) return

        // Snapshot current session before overwriting
        const currentSession = localStorage.getItem('docurious-session')
        const currentAuth = localStorage.getItem('docurious-auth')

        // Reset mock database to pristine state
        resetDatabase()

        // Log in as the chosen persona
        const success = await useAuthStore.getState().login({
          email: persona.email,
          password: persona.password,
        })

        if (success) {
          set({
            isActive: true,
            activePersonaKey: personaKey,
            savedSession: currentSession,
            savedAuthStore: currentAuth,
            isTransitioning: false,
          })
          const { activeContext, user } = useAuthStore.getState()
          navigate(getContextHomeRoute(activeContext, user ?? undefined), { replace: true })
        }
      },

      switchPersona: async (personaKey: string, navigate: NavigateFunction) => {
        const persona = getDemoPersona(personaKey)
        if (!persona) return

        set({ isTransitioning: true })

        const success = await useAuthStore.getState().login({
          email: persona.email,
          password: persona.password,
        })

        if (success) {
          set({ activePersonaKey: personaKey, isTransitioning: false })
          const { activeContext, user } = useAuthStore.getState()
          navigate(getContextHomeRoute(activeContext, user ?? undefined), { replace: true })
        } else {
          set({ isTransitioning: false })
        }
      },

      exitDemo: (navigate: NavigateFunction) => {
        const { savedSession, savedAuthStore } = get()

        // Restore previous session
        if (savedSession) {
          localStorage.setItem('docurious-session', savedSession)
        } else {
          localStorage.removeItem('docurious-session')
        }

        if (savedAuthStore) {
          localStorage.setItem('docurious-auth', savedAuthStore)
        } else {
          localStorage.removeItem('docurious-auth')
        }

        set({
          isActive: false,
          activePersonaKey: null,
          savedSession: null,
          savedAuthStore: null,
          isTransitioning: false,
        })

        navigate('/login', { replace: true })
      },

      resetDemoData: async () => {
        const { activePersonaKey } = get()
        const persona = activePersonaKey ? getDemoPersona(activePersonaKey) : null

        // Re-seed the database
        resetDatabase()

        // Re-login as current persona
        if (persona) {
          await useAuthStore.getState().login({
            email: persona.email,
            password: persona.password,
          })
        }
      },
    }),
    {
      name: 'docurious-demo-mode',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isActive: state.isActive,
        activePersonaKey: state.activePersonaKey,
        savedSession: state.savedSession,
        savedAuthStore: state.savedAuthStore,
      }),
    },
  ),
  shallow,
)
